import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search, CircleDot, PhoneCall, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Eyebrow, EASE } from "../components/site/Reveal";
import { API, api, formatApiError } from "../lib/api";

const field = "w-full bg-transparent border-0 border-b border-[#0A192F]/25 px-0 py-3 text-[#0A192F] placeholder:text-[#6B7385] focus:outline-none focus:border-[#8A6A2B] transition-colors duration-300";
const label = "font-mono text-[11px] uppercase tracking-[0.22em] text-[#5A6578]";
const STAGES = [
  { key: "new", title: "Received", text: "Your request is with our team.", icon: CircleDot },
  { key: "contacted", title: "In progress", text: "We've contacted you and are working on it.", icon: PhoneCall },
  { key: "resolved", title: "Completed", text: "Your EC request has been completed.", icon: CheckCircle2 },
];

const Result = ({ r }) => {
  const current = Math.max(0, STAGES.findIndex((s) => s.key === r?.status));
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
      className="mt-10 rounded-[24px] bg-[#0A192F] p-7 sm:p-10 text-[#F4EEE3]" data-testid="track-result">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] text-[#D9BD7E]">{r?.ref}</p>
          <h3 className="mt-2 font-display text-3xl sm:text-4xl">{r?.village}, {r?.mandal}</h3>
          <p className="mt-1 text-sm text-[#C9C3B6]">{r?.district} · {r?.purpose} · EC {r?.period_from} → {r?.period_to}</p>
        </div>
        <span className="rounded-full bg-[#C5A059] px-4 py-1.5 text-sm font-medium text-[#070F1E]" data-testid="track-status">{STAGES[current].title}</span>
      </div>
      <ol className="mt-10 grid gap-4 sm:grid-cols-3">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const reached = i <= current;
          return (
            <li key={s.key} className={`rounded-2xl border p-5 ${reached ? "border-[#C5A059]/60 bg-white/5" : "border-white/10"}`} data-testid={`track-stage-${s.key}`}>
              <Icon className={`h-5 w-5 ${reached ? "text-[#E3C88A]" : "text-[#8C8A86]"}`} />
              <p className={`mt-4 font-display text-2xl ${reached ? "text-[#F4EEE3]" : "text-[#A39D90]"}`}>{s.title}</p>
              <p className={`mt-1 text-sm ${reached ? "text-[#C9C3B6]" : "text-[#A39D90]"}`}>{s.text}</p>
            </li>
          );
        })}
      </ol>
      <p className="mt-6 text-xs text-[#A39D90]">Submitted {r?.created_at ? new Date(r.created_at).toLocaleString() : ""}</p>
    </motion.div>
  );
};

export default function TrackEC() {
  const [phone, setPhone] = useState("");
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post(`${API}/ec-requests/track`, { phone, ref });
      setResult(data ?? null);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHero testid="track-hero" eyebrow="Track EC request" lines={["Where is", <span className="italic text-[#E3C88A]">my EC?</span>]}
        intro="Enter the mobile number you used and the reference number you received after submitting your EC request." />
      <section className="paper py-20 sm:py-28" data-testid="track-section">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-8">
          <Eyebrow>Check status</Eyebrow>
          <form onSubmit={submit} className="mt-8 grid gap-8 sm:grid-cols-[1fr_1fr_auto] items-end rounded-[24px] bg-white border border-[#0A192F]/10 p-7 sm:p-10" data-testid="track-form">
            <label className="block"><span className={label}>Mobile number *</span>
              <input required minLength={7} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+91" className={field} data-testid="track-phone-input" /></label>
            <label className="block"><span className={label}>Reference no. *</span>
              <input required minLength={4} value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="EC-XXXXXX" className={`${field} font-mono tracking-wider`} data-testid="track-ref-input" /></label>
            <button type="submit" disabled={loading} data-testid="track-submit-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] px-7 py-3.5 text-sm text-[#F4EEE3] transition-[background-color] duration-300 hover:bg-[#13284A] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 text-[#E3C88A]" />} Track
            </button>
          </form>
          {error && <p className="mt-6 rounded-2xl bg-red-50 px-5 py-4 text-sm text-red-800" data-testid="track-error">{error}</p>}
          {result && <Result r={result} />}
          <p className="mt-10 text-sm text-[#4A5468]">Haven't submitted a request yet? <Link to="/encumbrance-certificate" className="underline underline-offset-4 text-[#8A6A2B]" data-testid="track-request-link">Request an EC through us</Link>.</p>
        </div>
      </section>
    </>
  );
}
